import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import Iconify from "../../../components/iconify";
import { LineItemsDetailTable } from "./ShopOrderComponents";
import {
  ORDER_STATUS_OPTIONS,
  ORDER_STATUSES_EDITABLE,
  FULFILLMENT_TYPE_META,
  getStatusMeta,
  getFulfillmentStatusMeta,
  getContractName,
  getContractEmail,
  getContractAddress,
  toCurrency,
} from "./shopUtils";

export default function ShopOrdersMobileList({
  visibleOrders,
  selectedOrderIds,
  expandedOrderId,
  onToggleOrderSelection,
  onToggleExpand,
  onOrderStatusChange,
  onCancelSingleOrder,
  onDeleteSingleOrder,
  onEditOrder,
  onUpdateFulfillment,
  runningAdminAction,
}) {
  return (
    <Stack spacing={1.5}>
      {visibleOrders.map((order) => {
        const isExpanded = expandedOrderId === order._id;
        const isSelected = selectedOrderIds.includes(order._id);
        const statusMeta = getStatusMeta(order.status);
        const fMeta = getFulfillmentStatusMeta(order.fulfillment?.status);
        const fType = order.fulfillment?.type || "shipping";
        const canEdit = ORDER_STATUSES_EDITABLE.has(order.status);
        const isLocalPending =
          fType === "local" &&
          order.fulfillment?.status !== "handed_out" &&
          order.status !== "canceled";
        const totalQty = (order.lineItems || []).reduce(
          (s, li) => s + Number(li.quantity || 0),
          0,
        );

        return (
          <Paper
            key={order._id}
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: isSelected ? "primary.main" : "divider",
              overflow: "hidden",
            }}
          >
            {/* ── Collapsed header ── */}
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{ p: 1.5, cursor: "pointer" }}
              onClick={() => onToggleExpand(order._id)}
            >
              <Checkbox
                checked={isSelected}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) =>
                  onToggleOrderSelection(order._id, e.target.checked)
                }
                size="small"
                sx={{ mt: -0.25 }}
              />

              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                {/* Row 1: Name + total */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                >
                  <Typography variant="subtitle2" noWrap sx={{ flexShrink: 1, minWidth: 0 }}>
                    {getContractName(order)}
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ flexShrink: 0, ml: 1 }}
                  >
                    {toCurrency(order?.totals?.grandTotal)}
                  </Typography>
                </Stack>

                {/* Row 2: Date + quantity */}
                <Typography variant="caption" color="text.secondary">
                  {dayjs(order.createdAt).format("DD.MM.YYYY HH:mm")} · {totalQty} Pos.
                </Typography>

                {/* Row 3: Status + fulfillment chips */}
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ mt: 0.5 }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Chip size="small" color={statusMeta.color} label={statusMeta.label} />
                  {fType !== "none" && (
                    <Chip
                      size="small"
                      variant="outlined"
                      color={fMeta.color}
                      label={fMeta.label}
                      icon={
                        <Iconify
                          icon={FULFILLMENT_TYPE_META[fType]?.icon || "mdi:truck-delivery-outline"}
                          width={14}
                        />
                      }
                    />
                  )}
                </Stack>
              </Box>

              <Iconify
                icon={isExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"}
                width={22}
                height={22}
                sx={{ color: "text.secondary", flexShrink: 0, mt: 0.25 }}
              />
            </Stack>

            {/* ── Line item chips (always visible) ── */}
            {Array.isArray(order.lineItems) && order.lineItems.length > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                useFlexGap
                flexWrap="wrap"
                sx={{ px: 1.5, pb: 1 }}
              >
                {order.lineItems.map((li, idx) => {
                  const variantPart =
                    li.variantLabel && li.variantLabel !== "Standard"
                      ? ` (${li.variantLabel})`
                      : "";
                  return (
                    <Chip
                      key={`${order._id}-${li.productId}-${li.variantKey}-${idx}`}
                      size="small"
                      variant="outlined"
                      label={`${li.productName}${variantPart} ×${li.quantity}`}
                    />
                  );
                })}
              </Stack>
            )}

            {/* ── Quick actions row (always visible, no expand needed) ── */}
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ px: 1.5, pb: 1.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Quick status change */}
              <FormControl size="small" sx={{ minWidth: 0 }}>
                <Select
                  value={order.status}
                  onChange={(e) => onOrderStatusChange(order._id, e.target.value)}
                  variant="outlined"
                  sx={{
                    fontSize: "0.75rem",
                    height: 30,
                    "& .MuiSelect-select": { py: 0.25, pr: "24px !important", pl: 1 },
                  }}
                  renderValue={() => (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Iconify icon="mdi:swap-vertical" width={16} sx={{ color: "text.secondary" }} />
                      <Typography variant="caption" fontWeight={600}>Status</Typography>
                    </Stack>
                  )}
                >
                  {ORDER_STATUS_OPTIONS.map((status) => {
                    const meta = getStatusMeta(status);
                    return (
                      <MenuItem key={status} value={status}>
                        {meta.label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {/* Quick local handout */}
              {isLocalPending && (
                <Tooltip title="Als ausgegeben markieren">
                  <IconButton
                    size="small"
                    color="success"
                    disabled={runningAdminAction}
                    onClick={() =>
                      onUpdateFulfillment({
                        id: order._id,
                        fulfillmentStatus: "handed_out",
                      })
                    }
                  >
                    <Iconify icon="mdi:hand-extended-outline" width={18} />
                  </IconButton>
                </Tooltip>
              )}

              {/* Quick edit */}
              {canEdit && (
                <Tooltip title="Bearbeiten">
                  <IconButton
                    size="small"
                    color="info"
                    disabled={runningAdminAction}
                    onClick={() => onEditOrder(order)}
                  >
                    <Iconify icon="mdi:pencil-outline" width={18} />
                  </IconButton>
                </Tooltip>
              )}

              <Box sx={{ flexGrow: 1 }} />

              {/* Expand toggle */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => onToggleExpand(order._id)}
              >
                {isExpanded ? "Weniger" : "Details"}
              </Typography>
            </Stack>

            {/* ── Expanded detail panel ── */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Box sx={{ px: 1.5, pb: 1.5 }}>
                <Divider sx={{ mb: 1.5 }} />

                {/* Line items detail table */}
                <LineItemsDetailTable lineItems={order.lineItems} />

                {/* Order metadata */}
                <Stack spacing={0.25} sx={{ mt: 1.5, mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    ID: {order._id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    E-Mail: {getContractEmail(order)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Adresse: {getContractAddress(order)}
                  </Typography>
                  {order.fulfillment?.trackingNumber && (
                    <Typography variant="caption" color="text.secondary">
                      Sendungsnr.: {order.fulfillment.trackingNumber}
                    </Typography>
                  )}
                </Stack>

                {/* Local handout full button */}
                {isLocalPending && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    fullWidth
                    disabled={runningAdminAction}
                    onClick={() =>
                      onUpdateFulfillment({
                        id: order._id,
                        fulfillmentStatus: "handed_out",
                      })
                    }
                    startIcon={<Iconify icon="mdi:hand-extended-outline" width={16} />}
                    sx={{ mb: 1 }}
                  >
                    Als ausgegeben markieren
                  </Button>
                )}

                {/* Full action buttons */}
                <Stack spacing={1}>
                  {/* Edit button */}
                  {canEdit && (
                    <Button
                      size="small"
                      variant="contained"
                      color="info"
                      fullWidth
                      disabled={runningAdminAction}
                      onClick={() => onEditOrder(order)}
                      startIcon={<Iconify icon="mdi:pencil-outline" width={16} />}
                    >
                      Bearbeiten
                    </Button>
                  )}

                  {/* Status change (full Select) */}
                  <FormControl size="small" fullWidth>
                    <Select
                      value={order.status}
                      onChange={(e) => onOrderStatusChange(order._id, e.target.value)}
                    >
                      {ORDER_STATUS_OPTIONS.map((status) => {
                        const meta = getStatusMeta(status);
                        return (
                          <MenuItem key={status} value={status}>
                            {meta.label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  {/* Destructive actions */}
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      fullWidth
                      disabled={order.status === "canceled" || runningAdminAction}
                      onClick={() => onCancelSingleOrder(order)}
                      startIcon={<Iconify icon="mdi:cancel" width={16} />}
                    >
                      Stornieren
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      fullWidth
                      disabled={runningAdminAction}
                      onClick={() => onDeleteSingleOrder(order)}
                      startIcon={<Iconify icon="eva:trash-2-outline" width={16} />}
                    >
                      Löschen
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Collapse>
          </Paper>
        );
      })}
    </Stack>
  );
}
